import React, {
    FC, memo, useEffect, useState
} from 'react';
import equal from 'fast-deep-equal/react';
import toReact from 'svelte-adapter/react';
import classnames from 'classnames';
import { Dialog } from '@wg/wows-react-uikit';
import { DWH_EVENTS } from '@wg/wows-commerce/constants/dwhEvents';
import { ProductEntity } from '@wg/wows-commerce/core/entity/product.entity';
import SvelteFastGoldShowcase, { FastGoldOneClickShowcaseProps } from '@wg/wows-commerce/ui/FastGoldOneClick';

import styles from './BuyGoldDialog.scss';
import type { RootState } from '~/store';
import type { Wallet } from '~/types';
import preloaded from '~/preloaded';
import { useAppDispatch, useAppSelector } from '~/hooks';
import { wowsCommerce } from '~/wowsCommerce';
import { openExternalUrl } from '~/routerHistory';
import { closeBuyGoldDialog } from '~/store/dialogs';
import { loadProducts } from '~/store/wowsCommerce';
import { disableBlur, enableBlur } from '~/store/app';


export interface BuyGoldDialogState {
    isBuyGoldDialogVisible: boolean,
    isSteambotBackButtonVisible: boolean,
    isWsmartHostProvided: boolean,
    products?: Array<ProductEntity>,
    walletData: Wallet,
}

const stateSelector = (state: RootState): BuyGoldDialogState => {
    return {
        isBuyGoldDialogVisible: state.dialogs.isBuyGoldDialogVisible,
        isSteambotBackButtonVisible: state.app.isSteambotBackButtonVisible,
        isWsmartHostProvided: !!preloaded.urls.wsmartUrl,
        products: state.wowsCommerce.products,
        walletData: state.app.account?.wallet,
    };
};

const FastGold = toReact(SvelteFastGoldShowcase, {}, 'div') as FC<FastGoldOneClickShowcaseProps>;

const BuyGoldDialog = () => {
    const [selectedProduct, setSelectedProduct] = useState(null as ProductEntity | null);

    const dispatch = useAppDispatch();
    const state = useAppSelector(stateSelector, equal);

    const { isBuyGoldDialogVisible, products, walletData } = state;
    const showArmoryUrl = preloaded.urls.armoryUrl.length > 0;

    useEffect(() => {
        return () => {
            if (isBuyGoldDialogVisible) {
                dispatch(disableBlur());
            } else {
                dispatch(enableBlur());
                dispatch(loadProducts());
            }
        };
    }, [dispatch, isBuyGoldDialogVisible]);

    const openMoreBundles = () => {
        openExternalUrl(preloaded.urls.armoryUrl);
    };

    const removeBlur = () => {
        setSelectedProduct(null);
    };

    const isPopupOpened = selectedProduct !== null;

    const hideDialog = React.useCallback((force = false) => {
        if (!isPopupOpened || force) {
            dispatch(closeBuyGoldDialog());
        }
    }, [isPopupOpened, dispatch]);

    const onBeforePurchase = React.useCallback((event: { detail: { product: ProductEntity } }) => {
        wowsCommerce.analytics.addEvent(DWH_EVENTS.OPEN_PURCHASE_POPUP_FROM_CATEGORY, {
            bundle_id: event.detail.product.externalProductId
        });
    }, []);

    const onPurchaseResult = React.useCallback(() => {
        hideDialog(true);
        setTimeout(removeBlur, 200);
    }, [hideDialog]);

    const dialogContent = React.useMemo(() => {
        if (!products) {
            return null;
        }

        return (
            <div className={styles.wrapper}>
                <FastGold
                    products={products}
                    storefrontName={preloaded.settings.wsmartStorefront}
                    goldBalance={walletData?.gold ?? undefined}
                    onBeforePurchase={onBeforePurchase}
                    onPurchaseSuccess={onPurchaseResult}
                    onPurchaseError={onPurchaseResult}
                    onOpenMoreBundles={showArmoryUrl ? openMoreBundles : undefined}
                    onClose={hideDialog}
                />
            </div>
        );
    }, [products, walletData?.gold, hideDialog, onBeforePurchase, onPurchaseResult, showArmoryUrl]);

    return (
        <Dialog
            className={classnames(styles.dialog, { [styles.blurredDialog]: isPopupOpened })}
            content={dialogContent}
            size={'worship'}
            isOverlay={false}
            isVisible={isBuyGoldDialogVisible}
            hideDialog={hideDialog}
        />
    );
};

export default memo(BuyGoldDialog);
